import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SG_SURV_ITM_LIST, SF_SURV_ITM_INFO } from "./services/PtliSurvItmMgmtService";
import { PTLI0418001Q } from "../qustnr/PTLI0418001Q";
import { PTLI0419001Q } from "../ques/PTLI0419001Q";

/*
 * Survey Item Detail
 * !== 설문 항목 상세 ==!
 */

export const PTLI0420004Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const { quesItmId } = useParams();

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survItmInfo: useForm({
            defaultSchema: SF_SURV_ITM_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {};

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvItmInfo: useFetch({
            api: (data) => APIS.getSurvItmInfo(quesItmId),
            enabled: !!quesItmId,
            onSuccess: (data) => {
                const survItmInfo = data.survItmInfo.content;
                form.survItmInfo.setValues(survItmInfo);

                const { content } = data.survItmInfo || {};
                if (comnUtils.isEmpty(content)) {
                modal.openModal({ content: "msg.ptl.00694" }); // 비정상 접근입니다.
                navigate(URLS.ptli0420001q);
                return;
                }
            
                form.survItmInfo.setValues(content);

                const { survItmLangList } = content;
                    if (!comnUtils.isEmpty(survItmLangList)) {
                        survItmLangList.forEach((row: any) => {
                            const { langCd, itmCn } = row;
                            if (!comnUtils.isEmpty(langCd)) {
                                form.survItmInfo.setValue(`itmCn${langCd}`, itmCn ?? "");
                            }
                    });
                }
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteSurvItmInfo: useFetch({
            api: (quesItmId) => APIS.deleteSurvItmInfo(quesItmId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(URLS.ptli0420001q);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),
    };
    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        deleteSurvItmInfo: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteSurvItmInfo.fetch(quesItmId);
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getSurvItmInfo.fetch();
        form.survItmInfo.setEditable(false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmInfo.schema.qustnrTtle}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmInfo.setValue("qustnrTtle", item.qustnrTtle);
                                                form.survItmInfo.setValue("qestnrId", item.qestnrId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0418001Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmInfo.schema.quesCn}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmInfo.setValue("quesCn", item.quesCn);
                                                form.survItmInfo.setValue("quesId", item.quesId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0419001Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmOrd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmCnEN} />
                            <Group.Control {...form.survItmInfo.schema.itmCnTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmCnKO} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button
                        role={"list"}
                        onClick={() => {
                            navigate(URLS.ptli0420001q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="delete"
                        onClick={() => {
                            handler.deleteSurvItmInfo();
                        }}
                    ></Button>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.ptli0420003s}/${quesItmId}`);
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
